import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Newspaper, Settings, Users, Video, Image, LogOut,
  Menu, X, Palette, ImagePlus, Mail, Camera, MessageSquare, Bell, Cog,
} from 'lucide-react';
import { useSiteSettings } from '../../hooks/useSiteSettings';

const TABS = [
  { id: 'dashboard', label: 'सिंहावलोकन', icon: LayoutDashboard, path: '/admin/dashboard' },
  { id: 'news', label: 'समाचार', icon: Newspaper, path: '/admin/news' },
  { id: 'categories', label: 'श्रेणीहरू', icon: Settings, path: '/admin/categories' },
  { id: 'subscribers', label: 'सब्सक्राइबर', icon: Users, path: '/admin/subscribers' },
  { id: 'reels', label: 'रिल्स', icon: Video, path: '/admin/reels' },
  { id: 'ads', label: 'विज्ञापन', icon: Image, path: '/admin/ads' },
  { id: 'team', label: 'हाम्रो टिम', icon: Users, path: '/admin/ourteam' },
  { id: 'allphotos', label: 'फोटाहरू', icon: Camera, path: '/admin/allphotos' },
  { id: 'contacts', label: 'सम्पर्क सन्देश', icon: Mail, path: '/admin/contacts' },
  { id: 'comments', label: 'कम्मेन्टहरू', icon: MessageSquare, path: '/admin/comments' },
  { id: 'notice', label: 'नोटिस मोडल', icon: Bell, path: '/admin/notice' },
  { id: 'logos', label: 'लोगो अपडेट', icon: ImagePlus, path: '/admin/logos' },
  { id: 'footer', label: 'फुटर सेटिङ', icon: Palette, path: '/admin/footer' },
  { id: 'setting', label: 'सेटिङ', icon: Cog, path: '/admin/setting' },
];

// MiniNav jastai emerald theme
function AdminNav({ onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav className="flex flex-col gap-1.5">
      {TABS.map((t) => {
        const active = location.pathname === t.path || location.pathname.startsWith(`${t.path}/`);
        return (
          <button
            key={t.id}
            className={`group flex items-center gap-3 px-4 py-2.5 sm:py-3 rounded-xl text-[13.5px] font-semibold text-left transition-all ${
              active
                ? 'bg-crimson text-white shadow-lg shadow-crimson/25'
                : 'text-ink-soft hover:text-ink hover:bg-white'
            }`}
            onClick={() => {
              navigate(t.path);
              if (onNavigate) onNavigate();
            }}
          >
            <span
              className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors flex-shrink-0 ${
                active ? 'bg-white/20' : 'bg-emerald-200/60 group-hover:bg-white'
              }`}
            >
              <t.icon size={15} />
            </span>
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

function AdminBrand({ compact }) {
  const settings = useSiteSettings();
  
  return (
    <div className={`flex items-center gap-2.5 ${compact ? 'px-1 py-1' : 'px-6 py-6'}`}>
      {settings.header_logo ? (
        <img 
          src={settings.header_logo} 
          alt="Kalo Jhola" 
          className={`w-auto object-contain ${compact ? 'h-10' : 'h-14'}`}
        />
      ) : (
        <span className="font-display text-[18px] font-bold flex items-center gap-2 text-ink">
          KALO<em className="not-italic text-crimson">JHOLA</em>
        </span>
      )}
      {!compact && (
        <span className="font-body text-[10.5px] font-bold bg-gold text-ink px-2 py-0.5 rounded-full tracking-[0.03em]">
          एडमिन
        </span>
      )}
    </div>
  );
}

// Logout button — desktop sidebar ra mobile panel dubai ma use hune
function LogoutButton({ adminName, onLogout }) {
  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-9 h-9 rounded-full bg-crimson text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
          {String(adminName).trim().charAt(0).toUpperCase() || 'A'}
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-ink truncate">{adminName}</span>
          <span className="block text-[11px] text-muted">प्रबन्धक</span>
        </span>
      </div>
      <button
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-300 text-[13px] font-semibold text-ink-soft hover:text-ink hover:bg-white transition-colors"
        onClick={onLogout}
      >
        <LogOut size={14} /> लगआउट
      </button>
    </>
  );
}

function AdminLayout() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  let adminName = 'Admin';
  try {
    const data = JSON.parse(localStorage.getItem('adminData') || '{}');
    adminName = data.name || data.email || 'Admin';
  } catch {
    adminName = 'Admin';
  }

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-paper-soft w-full overflow-x-hidden">
      {/* Desktop: fixed full-height sidebar (mini nav jastai emerald) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[250px] bg-emerald-100 border-r border-emerald-200 flex-col z-40 shadow-lg">
        <AdminBrand />
        <div className="flex-1 overflow-y-auto py-2 px-3">
          <AdminNav />
        </div>
        <div className="border-t border-emerald-200 p-4">
          <LogoutButton adminName={adminName} onLogout={handleLogout} />
        </div>
      </aside>

      {/* Mobile: compact top bar + tala-tira slide hune menu panel */}
      <div className="lg:hidden sticky top-0 z-30 bg-emerald-100 border-b border-emerald-200">
        <header className="flex items-center justify-between gap-3 px-4 py-2.5">
          <AdminBrand compact />
          <button
            className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] text-ink hover:bg-white"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>

        {/* Sliding down/up panel — mobile ma sidebar yahi bata khulchha */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            menuOpen ? 'max-h-[80vh] opacity-100 shadow-xl' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-3 pt-1 pb-2 max-h-[80vh] overflow-y-auto">
            <AdminNav onNavigate={() => setMenuOpen(false)} />
          </div>
          <div className="border-t border-emerald-200 p-4">
            <LogoutButton adminName={adminName} onLogout={() => { setMenuOpen(false); handleLogout(); }} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="lg:ml-[250px] min-w-0">
        <main className="max-w-[1320px] mx-auto p-4 sm:p-5 md:p-8 pb-16">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
